<template>
  <b-card no-body>
    <b-table
      id="coupons"
      ref="table"
      class="bg-white"
      responsive
      show-empty
      :fields="fields"
      :items="items"
    >
      <template #empty>
        <p align="center">
          <span v-if="!busy">{{ trans('none') }}</span>
          <span v-if="busy">
            <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
            {{ trans('please_wait') }}
          </span>
        </p>
      </template>
      <template #cell(code)="data">
        <coupon :coupon="data.item"></coupon>
      </template>
      <template #cell(expired_at)="data">
        <small>
          {{ data.value }}
        </small>
      </template>
      <template #cell(redeemed_at)="data">
        <small>
          {{ data.value }}
        </small>
      </template>
      <template #cell(used_on)="data">
        <small>
          {{ data.item.invoiceItemName }}
        </small>
      </template>
      <template #cell(action)="data">
        <b-dropdown
            v-if="canSeeDropDown(data.item)"
            variant="link"
            size="sm"
            class="p-0"
            toggle-class="text-muted"
            boundary="window"
            right
            :disabled="busy"
          >
            <template #button-content>
              <i class="fa fa-fw fa-ellipsis-h"></i>
            </template>
            <b-dropdown-item
              v-if="canSeeCancel(data.item)"
              @click="cancelClass(data.item)"
            >
              {{ trans('cancel') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canSeeDropInForm(data.item)"
              @click="viewDropInForm(data.item)"
            >
              {{ trans('view_drop_in_form') }}
            </b-dropdown-item>
        </b-dropdown>
      </template>
      <template #cell(applies_to)="data">
        <div v-for="pkg in data.item.packages" :key="pkg.id">
          <a :href="`${iframeUrlPrefix}/org/${organization.id}/package/${pkg.id}`" target="_blank">
            <small>
              {{ pkg.name }}
              <i class="fa fa-fw fa-external-link-alt"></i>
            </small>
          </a>
        </div>
        <div v-for="product in data.item.products" :key="product.id">
          <a :href="`${iframeUrlPrefix}/org/${organization.id}/products#product-card-${product.id}`" target="_blank">
            <small>
              {{ product.name }}
              <i class="fa fa-fw fa-external-link-alt"></i>
            </small>
          </a>
        </div>
        <div v-for="cls in (data.item.showMoreClasses ? data.item.am_classes : data.item.am_classes.slice(0, 3))" :key="cls.id">
          <small>
            {{ cls.name }}
          </small>
        </div>
        <b-button
          v-if="data.item.am_classes.length > 3"
          variant="link"
          size="sm"
          class="p-0 text-muted shadow-none"
          @click="data.item.showMoreClasses = !data.item.showMoreClasses"
        >
          <small>{{ trans(data.item.showMoreClasses ? 'less' : 'more').toLowerCase() }}...</small>
        </b-button>
      </template>
    </b-table>
    <div v-if="redeemedCount > 0" class="pb-4 pt-0 text-center">
      <b-button variant="link" @click="show_redeemed = ! show_redeemed">
        {{ trans('redeemed') }} ({{ redeemedCount }})
        <i :class="`fa fa-fw fa-caret-${show_redeemed ? 'up' : 'down'}`"></i>
      </b-button>
    </div>
  </b-card>
</template>

<script>
  import Coupon from './../coupon/Coupon'

  export default {
    props: {
      organization: Object,
      initialCoupons: Array,
      hideRedeemed: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      Coupon,
    },
    data() {
      return {
        trans: trans,
        iframeUrlPrefix: _.iframeUrlPrefix,
        busy: false,
        error: null,
        show_redeemed: false,
        coupons: this.initialCoupons || null,
        cancelling: false,
      }
    },
    computed: {
      items() {
        let redeemed = []
        let coupons = _.reduce(this.coupons || [], (coupons, coupon) => {
          if (!coupon.is_redeemed) coupons.push(coupon)
          else redeemed.push(coupon)
          return coupons
        }, [])
        return this.show_redeemed ? _.union(coupons, redeemed) : coupons
      },
      redeemedCount() {
        return _.reduce(this.coupons || [], (count, coupon) => {
          if (coupon.is_redeemed) count += 1
          return count
        }, 0)
      },
      hasExpiredAt() {
        return _.reduce(this.coupons || [], (has, c) => {
          if (!!c.expired_at) has = true
          return has
        }, false)
      },
      fields() {
        return _.compact([
          {key: 'code', label: trans('code')},
          !this.hasExpiredAt ? null : {
            key: 'expired_at',
            sortable: true,
            label: trans('expires_at'),
            formatter(value, key, item) {
              return value ? moment(value).format('LL') : null
            },
          },
          this.hideRedeemed ? null : {
            key: 'redeemed_at',
            label: trans('redeemed_at'),
            sortable: true,
            formatter(value, key, item) {
              return value ? moment(value).format('LLL') : null
            },
          },
          this.hideRedeemed ? null : {
            key: 'invoiceItemName',
            label: trans('used_on'),
          },
          {
            key: 'applies_to',
            label: trans('applies_to'),
            class: 'table-row-width-200'
          },
          {key: 'action', label: ''},
        ])
      },
    },
    methods: {
      canSeeDropDown(item) {
        return this.canSeeCancel(item) || this.canSeeDropInForm(item)
      },
      canSeeCancel(item) {
        return item.am_classes.length > 0 && item.rules && item.rules.allow_client_refund && !item.redeemed_at
      },
      canSeeDropInForm(item) {
        return item.am_classes.length > 0 && item.meta && item.meta.drop_in_form_url
      },
      viewDropInForm(item) {
        let url = item.meta.drop_in_form_url;
        let index = url.indexOf('?r=')
        if (index > 0) {
          url = url.substring(0, index)
        }
        window.open(url, '_blank')
      },
      onClickCancel(coupon) {
        this.cancelling = true
        this.$bvModal.msgBoxConfirm(trans('are_you_sure'), {
            title: trans('cancel_coupon'),
            okTitle: trans('yes'),
            okVariant: 'success',
            cancelTitle: trans('cancel'),
          })
          .then(confirmed => {
            if (!confirmed) return
            this.busy = true
            this.error = null
            let url = `/org/${this.organization.id}/client/cancel_coupon`
            return axios.post(url, coupon)
            .then(response => {
              this.busy = false
              this.cancelling = false
              this.fetch()
            })
            .catch(error => {
              this.busy = false
              this.error = _.errorFromAjax(error)
              this.$bvToast.toast(this.error, {
                title: trans('error'),
                variant: 'danger',
                solid: true,
              })
            })
          })
      },
      fetch() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/client/coupons`
        return axios.get(url)
        .then(response => {
          this.busy = false
          let coupons = response.data.map((coupon) => {
            coupon.showMoreClasses = false
            return coupon
          })
          this.coupons = coupons
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          this.$bvToast.toast(this.error, {
            title: trans('error'),
            variant: 'danger',
            solid: true,
          })
          this.coupons = []
        })
      },
    },
    mounted() {
      window.myCoupons = this
      if (!this.coupons) this.fetch()
    },
  }
</script>